import { useEffect, useState } from "react";
import { getSummarizeJobByName, getTemplateByName } from "./patientApi";
import {
  getMostRecentSummarizeJob,
  getSummarizeJobTemplateNames,
} from "./summarizationJobUtils";

const emptyState = {
  loading: false,
  error: null,
  sourceJobName: null,
  convPromptName: null,
  startingNoteName: null,
  convPromptTemplate: null,
  startingNoteTemplate: null,
};

async function resolveTemplate(name) {
  if (!name) return null;
  const data = await getTemplateByName(name);
  if (data.error) {
    return { name, error: data.error };
  }
  return { name, ...data };
}

export function useSummarizeJobTemplates(summarization, open, summarizationLoading = false) {
  const [state, setState] = useState(emptyState);

  useEffect(() => {
    if (!open) {
      setState(emptyState);
      return undefined;
    }

    if (summarizationLoading) {
      setState({ ...emptyState, loading: true });
      return undefined;
    }

    if (summarization?.error) {
      setState({
        ...emptyState,
        error: summarization.error,
      });
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const latestJob = getMostRecentSummarizeJob(summarization);
      if (!latestJob?.summarize_job_name) {
        if (!cancelled) {
          setState({
            ...emptyState,
            error: "No summarization jobs found for this patient",
          });
        }
        return;
      }

      let job = latestJob;
      let { convPromptName, startingNoteName } = getSummarizeJobTemplateNames(job);

      if (!convPromptName && !startingNoteName) {
        const fullJob = await getSummarizeJobByName(latestJob.summarize_job_name);
        if (cancelled) return;
        if (!fullJob.error) {
          job = fullJob;
          ({ convPromptName, startingNoteName } = getSummarizeJobTemplateNames(job));
        }
      }

      if (!convPromptName && !startingNoteName) {
        if (!cancelled) {
          setState({
            ...emptyState,
            sourceJobName: latestJob.summarize_job_name,
            error: "Latest summarization job has no template names",
          });
        }
        return;
      }

      const [convPromptTemplate, startingNoteTemplate] = await Promise.all([
        resolveTemplate(convPromptName),
        resolveTemplate(startingNoteName),
      ]);

      if (cancelled) return;

      setState({
        loading: false,
        error: null,
        sourceJobName: job.summarize_job_name || latestJob.summarize_job_name,
        convPromptName,
        startingNoteName,
        convPromptTemplate,
        startingNoteTemplate,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [open, summarization, summarizationLoading]);

  return state;
}
